import React, { useEffect, useState } from "react";
import { Alert, Button, Collapse, Form, Input, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { saveSettings } from "./api";
import { purposeLabels } from "./labels";
import {
  DEFAULT_TEMPLATES,
  MAX_SUBJECT,
  MAX_TEXT,
  MESSAGE_TYPES,
  PLACEHOLDERS,
  SAMPLE_VARS,
  render,
  smsInfo,
  templateFor,
  unknownPlaceholders
} from "./templates";

const TemplateEditor = ({ type, value, onChange, whatsappName, onWhatsappName }) => {
  const { t } = useTranslation("dashboard");
  const effective = templateFor(type, { [type]: value });
  const preview = render(effective.text, SAMPLE_VARS);
  const sms = smsInfo(preview);
  const unknown = [
    ...new Set([
      ...unknownPlaceholders(type, effective.subject),
      ...unknownPlaceholders(type, effective.text)
    ])
  ];
  return (
    <div className="nt-template">
      <Space size={[4, 4]} wrap className="nt-gap">
        <Typography.Text type="secondary">{t("Placeholders")}:</Typography.Text>
        {PLACEHOLDERS[type].map(key => (
          <Tag key={key} className="nt-mono">{`{{${key}}}`}</Tag>
        ))}
      </Space>
      <Form.Item label={t("Email subject")}>
        <Input
          value={value.subject}
          placeholder={DEFAULT_TEMPLATES[type].subject}
          maxLength={MAX_SUBJECT}
          onChange={e => onChange({ ...value, subject: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label={t("Text (email and SMS)")}
        extra={t("Leave empty to use the default text shown as placeholder.")}
      >
        <Input.TextArea
          value={value.text}
          placeholder={DEFAULT_TEMPLATES[type].text}
          maxLength={MAX_TEXT}
          autoSize={{ minRows: 3, maxRows: 10 }}
          onChange={e => onChange({ ...value, text: e.target.value })}
        />
      </Form.Item>
      {unknown.length > 0 && (
        <Alert
          className="nt-gap"
          type="warning"
          showIcon
          message={t("Unknown placeholders stay empty: {{list}}", { list: unknown.join(", ") })}
        />
      )}
      <div className="nt-preview">
        <Typography.Text type="secondary" className="nt-small">
          {t("Preview")} · {t("SMS: {{characters}} characters, {{parts}} part(s)", sms)}
        </Typography.Text>
        <div className="nt-preview-text">{preview}</div>
      </div>
      <Form.Item
        label={t("WhatsApp template name")}
        extra={t(
          "Name of the template approved by Meta. Its body parameters 1, 2, 3 … receive the placeholders in the order shown above. Empty = not sent by WhatsApp."
        )}
      >
        <Input
          value={whatsappName}
          maxLength={120}
          placeholder="z. B. termin_erinnerung"
          onChange={e => onWhatsappName(e.target.value.trim().toLowerCase())}
        />
      </Form.Item>
      {(value.subject || value.text) && (
        <Button size="small" onClick={() => onChange({ subject: "", text: "" })}>
          {t("Reset to default")}
        </Button>
      )}
    </div>
  );
};

const TemplateSettings = ({ settings, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const labels = purposeLabels(t);
  const [templates, setTemplates] = useState(settings.templates);
  const [whatsapp, setWhatsapp] = useState(settings.whatsapp.templates);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTemplates(settings.templates);
    setWhatsapp(settings.whatsapp.templates);
  }, [settings]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await saveSettings({ templates, whatsapp: { templates: whatsapp } });
      Success(t("Settings saved"));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form layout="vertical">
      <Typography.Paragraph type="secondary">
        {t(
          "The messages are written in German, the language of your customers' documents. Placeholders are filled in when the message is sent."
        )}
      </Typography.Paragraph>
      <Collapse
        className="nt-gap"
        items={MESSAGE_TYPES.map(type => ({
          key: type,
          label: (
            <Space wrap>
              <span>{labels[type]}</span>
              {(templates[type]?.subject || templates[type]?.text) && (
                <Tag color="blue">{t("Customized")}</Tag>
              )}
              {whatsapp[type] && <Tag color="green">WhatsApp</Tag>}
            </Space>
          ),
          children: (
            <TemplateEditor
              type={type}
              value={templates[type] || { subject: "", text: "" }}
              onChange={value => setTemplates(prev => ({ ...prev, [type]: value }))}
              whatsappName={whatsapp[type] || ""}
              onWhatsappName={value => setWhatsapp(prev => ({ ...prev, [type]: value }))}
            />
          )
        }))}
      />
      <div className="nt-actions">
        <Button type="primary" loading={busy} onClick={save}>
          {t("Save")}
        </Button>
      </div>
    </Form>
  );
};

export default TemplateSettings;
